'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { SkeletonItem } from 'components/skeleton-item'
import { tw } from 'utils/classnames'
import dynamic from 'next/dynamic'
import { useUser } from '@repo/web-ui/hooks/use-user-data'
import { useHasAllAccessQuery } from '@repo/cf-user/hooks/use-user-data'
import { MyDowmoalds } from './my-downloads'
import { PostProjectSection } from './post-project-section'
import { RecommendedClubhouses } from '@web/app/[locale]/clubhouse/[slug]/_components/sidebar/recommended-clubhouses'
import { usePathname } from 'next/navigation'

const ClientOnlyImageAds = dynamic(() => import('./image-ads'), {
  ssr: false,
  loading: ImageSkeleton,
})
export function RightSidebar({
  className,
  showDownloads = true,
  showSidebar = true,
}: {
  className?: string
  showDownloads?: boolean
  showSidebar?: boolean
}) {
  const t = useTranslations('social-feed.components.right_sidebar')
  const { isPending } = useUser()
  const { data: userHasAllAccess } = useHasAllAccessQuery()

  const pathname = usePathname()
  const isDiscussionsPage = React.useMemo(() => pathname.includes('/discussions/'), [pathname])

  if (isPending) {
    return <RightSidebarSkeleton className={className} showDownloads={showDownloads} />
  }

  const isShowAd = !userHasAllAccess

  if (!showSidebar && !isShowAd) {
    return null
  }

  return (
    <section className={tw('hidden w-[256px] shrink-0 self-stretch xl:block', className)}>
      <div
        className="
          sticky top-5
          max-h-[calc(100vh-20px)]
          overflow-auto
        "
      >
        {isDiscussionsPage ? (
          <div className="mb-4">
            <RecommendedClubhouses />
          </div>
        ) : null}

        {userHasAllAccess && !isDiscussionsPage && showSidebar ? <PostProjectSection /> : null}
        {showDownloads && !userHasAllAccess && showSidebar ? <MyDowmoalds /> : null}

        {isShowAd ? (
          <div className="flex w-[256px] flex-col gap-4">
            <div className="border-[#1313151F] rounded-[12px] border p-3 bg-cf-white">
              <p className="text-cf-purple-900 text-12 inline-block pb-3 font-medium uppercase">
                {t('sidebar_title')}
              </p>
              <ClientOnlyImageAds />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function ImageSkeleton() {
  return <SkeletonItem className="h-[203px] w-full" />
}

export function RightSidebarSkeleton({
  className,
  showDownloads = true,
}: {
  className?: string
  showDownloads?: boolean
}) {
  return (
    <section className={tw('relative hidden w-[256px] shrink-0 self-stretch xl:block', className)}>
      <div className="flex w-[256px] flex-col gap-4">
        {showDownloads ? (
          /* MyDowmoalds skeleton */
          <div className="rounded-[12px] border border-[#1313151F] bg-cf-white p-3">
            <SkeletonItem className="h-4 w-24 rounded" />
            <div className="mt-3 flex flex-col gap-2">
              <SkeletonItem className="h-8 w-full rounded" />
              <SkeletonItem className="h-8 w-full rounded" />
              <SkeletonItem className="h-8 w-3/4 rounded" />
            </div>
          </div>
        ) : null}
        {/* Ad card skeleton */}
        <div className="rounded-[12px] border border-[#1313151F] bg-cf-white p-3">
          <SkeletonItem className="h-3 w-28 rounded" />
          <div className="mt-3 flex flex-col gap-3">
            <SkeletonItem className="h-[203px] w-full rounded-[8px]" />
          </div>
        </div>
      </div>
    </section>
  )
}
